import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { Tooltip } from "primereact/tooltip";

import LogoCompact from "@/assets/icons/logo-hub-compact.svg?react";
import LogoFull from "@/assets/icons/logo-hub-full.svg?react";
import { Icon } from "@/ds";

import {
  NAVIGATOR_DRAWER_LABEL,
  NAVIGATOR_LOGO_LABEL,
  NAVIGATOR_MAIN_LABEL,
  NAVIGATOR_OPEN_DRAWER_LABEL,
  NAVIGATOR_SECONDARY_LABEL,
} from "./navigator.constants";
import styles from "./navigator.module.scss";
import { NavigatorUser } from "./navigator-user";
import type {
  NavigatorEntry,
  NavigatorItemIcon,
  NavigatorPanelState,
  NavigatorViewModel,
} from "./types-navigator";

/** Recorte do logo sem o respiro do frame de 135×45 (o header mobile desenha só a marca, 81×31). */
const LOGO_MARK_VIEWBOX = "22 5 88 34";

/** hamburger-menu (7203-200510): 3 barras de 18×2 em `currentColor`, gap 4. */
const HAMBURGER_ICON = (
  <span className={styles.hamburgerIcon} aria-hidden>
    <span className={styles.hamburgerBar} />
    <span className={styles.hamburgerBar} />
    <span className={styles.hamburgerBar} />
  </span>
);

/** Aceita tanto o nome de um ícone do DS quanto um SVG do projeto. */
function ItemIcon({ icon }: { icon: NavigatorItemIcon }) {
  if (typeof icon === "string") return <Icon name={icon} className={styles.itemIcon} />;

  const Svg = icon;
  return <Svg className={styles.itemIcon} aria-hidden focusable="false" />;
}

function ItemList({ entries, onNavigate }: { entries: NavigatorEntry[]; onNavigate: () => void }) {
  return (
    <ul className={styles.list}>
      {entries.map((entry) => (
        <li key={entry.path}>
          <Link
            to={entry.path}
            onClick={onNavigate}
            className={styles.item}
            data-active={entry.active || undefined}
            aria-current={entry.ariaCurrent}
            data-pr-tooltip={entry.tooltip}
            data-testid={entry.testId}
          >
            <ItemIcon icon={entry.icon} />
            <span className={styles.itemLabel}>{entry.label}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** O menu de 222 (ou 83, recolhido) do Figma "novo menu" 708-22166: logo, itens, divisor, itens secundários, usuário e "<"/">". */
function NavigatorPanel({
  panel,
  onNavigate,
}: {
  panel: NavigatorPanelState;
  onNavigate: () => void;
}) {
  return (
    <aside
      ref={panel.ref}
      className={styles.panel}
      data-state={panel.state}
      data-testid={panel.testId}
      onKeyDown={panel.onKeyDown}
    >
      <div className={styles.menu}>
        <div className={styles.brand}>
          <LogoFull className={styles.logoFull} role="img" aria-label={NAVIGATOR_LOGO_LABEL} />
          <LogoCompact
            className={styles.logoCompact}
            role="img"
            aria-label={NAVIGATOR_LOGO_LABEL}
          />
        </div>

        <nav aria-label={NAVIGATOR_MAIN_LABEL} className={styles.nav}>
          <ItemList entries={panel.items} onNavigate={onNavigate} />
        </nav>

        {panel.hasSecondary ? (
          <>
            <hr className={styles.divider} aria-hidden />
            <nav aria-label={NAVIGATOR_SECONDARY_LABEL} className={styles.nav}>
              <ItemList entries={panel.secondaryItems} onNavigate={onNavigate} />
            </nav>
          </>
        ) : null}
      </div>

      <div className={styles.footer}>
        <NavigatorUser variant={panel.userVariant} testId={panel.userTestId} />
      </div>

      <Button
        type="button"
        severity="secondary"
        className={`p-button-nav-toggle ${styles.toggle}`}
        icon={<Icon name={panel.toggleIcon} size={14} />}
        onClick={panel.onToggle}
        aria-label={panel.toggleLabel}
        aria-expanded={panel.toggleExpanded}
        data-testid={panel.toggleTestId}
      />

      {panel.collapsed ? (
        <Tooltip target={`.${styles.item}[data-pr-tooltip]`} position="right" event="both" />
      ) : null}
    </aside>
  );
}

/**
 * Navegação do Hub (Figma "Menu do Hub" 4830-167448): no desktop, o menu lateral expandido ou recolhido; no mobile
 * (7203-200151), o header com hambúrguer, logo e avatar, e o menu numa gaveta (`Sidebar` do Prime).
 */
export function NavigatorView(vm: NavigatorViewModel) {
  return (
    <>
      <header className={styles.header} data-testid="navigator-header">
        <div className={styles.headerStart}>
          <Button
            id={vm.hamburgerId}
            type="button"
            text
            className="p-button-nav-hamburger"
            icon={HAMBURGER_ICON}
            onClick={vm.openDrawer}
            aria-label={NAVIGATOR_OPEN_DRAWER_LABEL}
            aria-expanded={vm.drawerOpen}
            data-testid="navigator-hamburger"
          />
          <LogoFull
            className={styles.logoMobile}
            viewBox={LOGO_MARK_VIEWBOX}
            role="img"
            aria-label={NAVIGATOR_LOGO_LABEL}
          />
        </div>

        <NavigatorUser variant="avatar" testId="navigator-header-user" />
      </header>

      <div className={styles.desktop}>
        <NavigatorPanel panel={vm.desktop} onNavigate={vm.closeDrawer} />
      </div>

      <Sidebar
        visible={vm.drawerOpen}
        onHide={vm.closeDrawer}
        onShow={vm.focusDrawer}
        role="dialog"
        aria-modal="true"
        aria-label={NAVIGATOR_DRAWER_LABEL}
        position="left"
        className="p-sidebar-menu"
        showCloseIcon={false}
        blockScroll
        content={() => <NavigatorPanel panel={vm.drawer} onNavigate={vm.closeDrawer} />}
        pt={{ mask: { "data-testid": "navigator-drawer-mask" } }}
      />
    </>
  );
}
